/**
 * useLoadingAnalytics.js
 *
 * Analytics hook for the post-Summary Loading Screen. Mirrors
 * useSummaryAnalytics.js exactly — same GA4 wrapper via react-ga4, same
 * IS_PRODUCTION guard, same dev console.group/table logging.
 */

import { useCallback } from "react";
import ReactGA from "react-ga4";

const IS_PRODUCTION =
  process.env.NODE_ENV === "production" &&
  typeof window !== "undefined" &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1";

const send = (eventName, params = {}) => {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

  if (IS_PRODUCTION) {
    ReactGA.event(eventName, cleanParams);
  } else {
    // eslint-disable-next-line no-console
    console.group(`%c[LoadingAnalytics] ${eventName}`, "color:#00838F;font-weight:bold;");
    // eslint-disable-next-line no-console
    if (Object.keys(cleanParams).length > 0) console.table(cleanParams);
    // eslint-disable-next-line no-console
    else console.log("(no params)");
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

const useLoadingAnalytics = () => {
  const trackStepView = useCallback((sessionId) => {
    send("loading_step_view", { session_id: sessionId });
  }, []);

  const trackReassuranceShown = useCallback((sessionId, elapsedMs) => {
    send("loading_reassurance_shown", { session_id: sessionId, elapsed_ms: elapsedMs });
  }, []);

  const trackRetryOfferShown = useCallback((sessionId, elapsedMs) => {
    send("loading_retry_offer_shown", { session_id: sessionId, elapsed_ms: elapsedMs });
  }, []);

  const trackRetryClick = useCallback((sessionId, attempt) => {
    send("loading_retry_click", { session_id: sessionId, attempt });
  }, []);

  const trackGenerationSuccess = useCallback((sessionId, durationMs, attempt) => {
    send("loading_generation_success", { session_id: sessionId, duration_ms: durationMs, attempt });
  }, []);

  const trackGenerationFailure = useCallback((sessionId, durationMs, attempt, message) => {
    send("loading_generation_failure", {
      session_id: sessionId,
      duration_ms: durationMs,
      attempt,
      message,
    });
  }, []);

  return {
    trackStepView,
    trackReassuranceShown,
    trackRetryOfferShown,
    trackRetryClick,
    trackGenerationSuccess,
    trackGenerationFailure,
  };
};

export default useLoadingAnalytics;
